import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Phone, MapPin, Mail, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getContactSettings } from '../lib/api';

const defaultContact = {
  phone: '0539 323 62 32',
  phone_raw: '+905393236232',
  address: 'Bahçelievler mh. 3034 sk. No:5 Isparta/Merkez',
  email: '',
  whatsapp: '',
  working_hours: '',
};

export const ContactSection = () => {
  const { t } = useTranslation();
  const [contact, setContact] = useState(defaultContact);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await getContactSettings();
        if (res.data) {
          setContact({ ...defaultContact, ...res.data });
        }
      } catch (err) {
        // Use defaults
      }
    };
    fetchContact();
  }, []);

  return (
    <section id="contact" className="py-16 md:py-24 bg-muted/30" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-12"
        >
          <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl">
            {t('contact_title')}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          {/* Phone Card */}
          <a
            href={`tel:${contact.phone_raw}`}
            className="flex items-center gap-4 p-5 bg-card rounded-xl border border-border hover:border-brand-accent/30 transition-colors"
            data-testid="contact-telefon"
          >
            <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
              <Phone size={20} className="text-brand-accent" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Telefon</p>
              <p className="text-base font-semibold font-numbers mt-0.5">{contact.phone}</p>
            </div>
          </a>

          {/* Address Card */}
          <div
            className="flex items-center gap-4 p-5 bg-card rounded-xl border border-border"
            data-testid="contact-adres"
          >
            <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
              <MapPin size={20} className="text-brand-accent" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Adres</p>
              <p className="text-base font-semibold mt-0.5">{contact.address}</p>
            </div>
          </div>

          {/* Email Card */}
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-4 p-5 bg-card rounded-xl border border-border hover:border-brand-accent/30 transition-colors"
              data-testid="contact-email"
            >
              <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-brand-accent" strokeWidth={2} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-base font-semibold mt-0.5">{contact.email}</p>
              </div>
            </a>
          )}

          {/* Working Hours Card */}
          {contact.working_hours && (
            <div
              className="flex items-center gap-4 p-5 bg-card rounded-xl border border-border"
              data-testid="contact-hours"
            >
              <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center flex-shrink-0">
                <Clock size={20} className="text-brand-accent" strokeWidth={2} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Çalışma Saatleri</p>
                <p className="text-base font-semibold mt-0.5">{contact.working_hours}</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
